import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/pro-solid-svg-icons";
import { faArrowRightFromBracket } from "@fortawesome/pro-light-svg-icons";
import SidebarItem from "./SidebarItem";
import SidebarCollapse from "./SidebarCollapse";
import OffoodLogo from "../brand/OffoodLogo";
import OffoodIcon from "../brand/OffoodIcon";
import MobileSidebar from "./MobileSidebar";
import useTour from "../../hooks/useTour";
import { useAuth } from "../../hooks/useAuth";
import useBreakpoint from "../../hooks/useBreakpoint";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useLangNavigate from "../../hooks/useLangNavigate";
import { isDemoMode } from "../../utils/demoApiInterceptor";
import Alert from "../utils/Alert";

function Sidebar({
  menuList,
  isClickable = false,
  count = false,
  itemClassName = "",
  className = "",
  title = null,
  description = null,
  showLogout = true,
  activation = null,
  collapsible = true,
  showMobileProgress = false,
}) {
  const navigate = useLangNavigate();
  const { isGtLaptop, isLtLaptop } = useBreakpoint();
  const { logout, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const controlledRestaurantName = useSelector(
    (state) => state.admin.controlledRestaurantName,
  );
  const controlledCompanyName = useSelector(
    (state) => state.admin.controlledCompanyName,
  );
  const { isActive: isTourActive } = useTour();
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const isDesktop = isGtLaptop();
  const canCollapse = isDesktop && collapsible;
  const effectiveExpanded = isTourActive ? false : isExpanded;
  const visibleMenuItems = menuList.filter((item) => !item.hide);
  const activeStepIndex = Math.max(
    0,
    visibleMenuItems.findIndex((item) => item.isActive)
  );
  const totalSteps = Math.max(1, visibleMenuItems.length);
  const progressPercent = Math.round(((activeStepIndex + 1) / totalSteps) * 100);

  return (
    <div
      id="sidebar"
      className={`${canCollapse && !effectiveExpanded ? "collapsed" : ""} ${className}`}
      onMouseEnter={() => canCollapse && !isTourActive && setIsExpanded(true)}
      onMouseLeave={() => canCollapse && setIsExpanded(false)}
    >
      <div className="sidebar-header">
        {effectiveExpanded || !canCollapse ? (
          <OffoodLogo width="100px" onClick={() => navigate("/")} />
        ) : (
          <OffoodIcon width="50px" onClick={() => navigate("/")} />
        )}
      </div>
      {controlledRestaurantName && (
        <Alert className="mb-2 sidebar-alert">
          <span
            dangerouslySetInnerHTML={{
              __html: t("dashboard.admin.controlledRestaurant", {
                name: controlledRestaurantName,
              }),
            }}
          />
        </Alert>
      )}
      {controlledCompanyName && (
        <Alert className="mb-2 sidebar-alert">
          <span
            dangerouslySetInnerHTML={{
              __html: t("dashboard.admin.controlledCompany", {
                name: controlledCompanyName,
              }),
            }}
          />
        </Alert>
      )}
      {isAuthenticated && (
        <FontAwesomeIcon
          icon={faBars}
          className="sidebar-burger"
          onClick={() => setMobileSidebarOpen(true)}
        />
      )}
      {(title || description) && (
        <div className="sidebar-text-span">
          {title && <h3 className="sidebar-title">{title}</h3>}
          {description && (
            <div className="p3 sidebar-description text-light">
              {description}
            </div>
          )}
          {showMobileProgress && (
            <div className="sidebar-mobile-progress">
              <div className="sidebar-mobile-progress-header">
                <span className="sidebar-mobile-progress-count">
                  {activeStepIndex + 1}/{totalSteps}
                </span>
              </div>
              <div className="sidebar-mobile-progress-track">
                <div
                  className="sidebar-mobile-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <div className="sidebar-list">
        {menuList
          .filter((m) => !m.hide)
          .map((item, index) => {
            if (item.children) {
              return (
                <SidebarCollapse
                  key={item.key || index}
                  item={item}
                  isClickable={isClickable}
                  isExpanded={effectiveExpanded || !canCollapse}
                />
              );
            }
            return (
              <SidebarItem
                key={item.key || index}
                item={item}
                isClickable={isClickable}
                count={count && index + 1}
                className={itemClassName}
              />
            );
          })}
        {isAuthenticated && showLogout && !isDemoMode() && (
          <SidebarItem
            item={{
              tKey: "dashboard.logout.",
              onClick: logout,
              icon: faArrowRightFromBracket,
            }}
            isClickable={isClickable}
            className={itemClassName}
          />
        )}
      </div>
      {isLtLaptop() && (
        <MobileSidebar
          className={`${isMobileSidebarOpen ? "open" : ""}`}
          menuList={menuList}
          onClose={() => setMobileSidebarOpen(false)}
          isClickable={isClickable}
          activationDetails={activation}
        />
      )}
    </div>
  );
}

export default Sidebar;
